/**
 * Variant Card Component
 *
 * Compact, single-select card for a base-model variant (image, option label, price).
 * Mirrors the grid product-card pattern but is driven by variant data from the
 * `/products/{id}/variants` endpoint rather than a separate REST fetch.
 *
 * @package
 */

import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { getPriceFromProduct, formatPrice } from '../helpers/priceHelpers';

const VariantCard = ( { variant, isSelected, onSelect, vatMode, disabled } ) => {
	const label = decodeHtmlEntities( variant.label || variant.title ) || __( 'Variant', 'at-vehicle-configurator' );
	const price = getPriceFromProduct( variant, vatMode );

	const handleKeyDown = e => {
		if ( disabled ) {
			return;
		}
		if ( e.key === 'Enter' || e.key === ' ' ) {
			e.preventDefault();
			onSelect();
		}
	};

	return (
		<div
			className={ `configurator-variant-card ${ isSelected ? 'selected' : '' } ${ disabled ? 'disabled' : '' }` }
			role="radio"
			tabIndex={ disabled ? -1 : 0 }
			onClick={ () => ! disabled && onSelect() }
			onKeyDown={ handleKeyDown }
			aria-checked={ isSelected }
			aria-disabled={ disabled }
			aria-label={ sprintf(
				/* translators: 1: variant name, 2: price */
				__( '%1$s, %2$s', 'at-vehicle-configurator' ),
				label,
				formatPrice( price )
			) }
		>
			{ variant.image && (
				<div className="configurator-variant-card-image-wrapper">
					<img src={ variant.image } alt={ label } loading="lazy" decoding="async" />
				</div>
			) }
			<div className="configurator-variant-card-content">
				<span className="configurator-variant-card-title">{ label }</span>
				{ price > 0 && (
					<span className="configurator-variant-card-price">{ formatPrice( price ) }</span>
				) }
			</div>
		</div>
	);
};

VariantCard.propTypes = {
	variant: PropTypes.shape( {
		id: PropTypes.number.isRequired,
		title: PropTypes.string,
		label: PropTypes.string,
		image: PropTypes.string,
		price: PropTypes.object,
	} ).isRequired,
	isSelected: PropTypes.bool.isRequired,
	onSelect: PropTypes.func.isRequired,
	vatMode: PropTypes.oneOf( [ 'including', 'excluding' ] ).isRequired,
	disabled: PropTypes.bool,
};

VariantCard.defaultProps = {
	disabled: false,
};

export default VariantCard;
